import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { FieldIssue, pick, PointmanCompany, PointmanCustomerDetailDTO } from '_lib/api/apiTypes.shim';

import { RootState } from '_lib/redux/store';
import { getTradeForNewCompany } from '_lib/utils/helpers';
import { ClearIssueItem, removeIssues } from '_lib/utils/validationErrorUtils';

import type { SharedPhoneCallContext, SwitchProviderCompanyPayload } from '_lib/types/PhoneCall.types';
import type { RequireAtLeastOne } from 'type-fest';

export type PhoneCallContext = Pick<
  SharedPhoneCallContext,
  | 'callOutcome'
  | 'internalNotes'
  | 'notes'
  | 'appointmentDateTime'
  | 'previousAppointmentDate'
  | 'sendEscalationEmail'
  | 'sendCompanyEmail'
  | 'serviceType'
  | 'trade'
> & {
  overridePreviousAppointmentDate: boolean;
  customer: PointmanCustomerDetailDTO | null;
};

export const toSharedPhoneCallContext = (phoneCallContext: PhoneCallContext): SharedPhoneCallContext =>
  pick(phoneCallContext, [
    'callOutcome',
    'internalNotes',
    'notes',
    'appointmentDateTime',
    'previousAppointmentDate',
    'sendEscalationEmail',
    'sendCompanyEmail',
    'serviceType',
    'trade'
  ]);

/* Subset of data that we can safely carry over upon a re-initialization from error */
const safeDataKeys = [
  'callOutcome',
  'internalNotes',
  'notes',
  'appointmentDateTime',
  'previousAppointmentDate',
  'sendEscalationEmail',
  'sendCompanyEmail',
  'serviceType',
  'trade',
  'overridePreviousAppointmentDate'
] as const satisfies (keyof PhoneCallContext)[];

type InitialPhoneCallContext = Partial<PhoneCallContext>;

const initPhoneCallContext = (ctx: InitialPhoneCallContext): PhoneCallContext => ({
  callOutcome: ctx.callOutcome,
  internalNotes: ctx.internalNotes || '',
  notes: ctx.notes || '',
  appointmentDateTime: ctx.appointmentDateTime || null,
  previousAppointmentDate: ctx.previousAppointmentDate || null,
  sendEscalationEmail: ctx.sendEscalationEmail !== undefined ? ctx.sendEscalationEmail : false,
  sendCompanyEmail: ctx.sendCompanyEmail !== undefined ? ctx.sendCompanyEmail : false,
  serviceType: ctx.serviceType || null,
  trade: ctx.trade || null,
  overridePreviousAppointmentDate: !!ctx?.overridePreviousAppointmentDate,
  customer: ctx.customer || null
});

interface PointmanPhoneCallSlice {
  phoneCallContext: PhoneCallContext;
  phoneCallFieldIssues: FieldIssue[];
  phoneCallNonFieldErrors: string[];
}

const initState = (phoneCallContext: InitialPhoneCallContext): PointmanPhoneCallSlice => ({
  phoneCallContext: initPhoneCallContext(phoneCallContext),
  phoneCallFieldIssues: [],
  phoneCallNonFieldErrors: []
});

// TODO: generator functions that allow us to reuse the logic for things like setting the issues, clearing issues, etc
export const pointmanPhoneCallSlice = createSlice({
  name: 'pointmanPhoneCallSlice',
  initialState: initState({}),
  reducers: {
    initialize: (
      state,
      action: PayloadAction<{ company: PointmanCompany; options?: { keepExistingCallData: boolean } }>
    ) => {
      const { company, options } = action.payload;

      const trade = getTradeForNewCompany(
        (options?.keepExistingCallData && state.phoneCallContext.trade) || null,
        company
      );

      return initState({
        ...(options?.keepExistingCallData ? pick(state.phoneCallContext, safeDataKeys) : {}),
        trade
      });
    },
    switchDifferentProviderCompany: (_state, action: PayloadAction<SwitchProviderCompanyPayload>) => {
      const { company, ...phoneCallContext } = action.payload || {};
      return initState({
        ...phoneCallContext,
        trade: getTradeForNewCompany(phoneCallContext.trade, company)
      });
    },
    /** Used to ensure we have a valid trade selected when switching companies */
    normalizeTradeFromCompany: (state, action: PayloadAction<PointmanCompany>) => {
      state.phoneCallContext = {
        ...state.phoneCallContext,
        trade: getTradeForNewCompany(state.phoneCallContext.trade, action.payload)
      };
    },
    setPartialPhoneCallContext(
      state,
      action: PayloadAction<{
        updates: RequireAtLeastOne<Partial<PhoneCallContext>>;
        clearIssues?: ClearIssueItem[];
      }>
    ) {
      state.phoneCallContext = { ...state.phoneCallContext, ...action.payload.updates };
      if (state.phoneCallFieldIssues.length && action.payload.clearIssues?.length) {
        const updatedIssues = action.payload.clearIssues.reduce(
          (acc, item) => removeIssues({ ...(typeof item === 'string' ? { issuePath: item } : item), issues: acc }),
          state.phoneCallFieldIssues
        );
        if (updatedIssues.length !== state.phoneCallFieldIssues.length) state.phoneCallFieldIssues = updatedIssues;
      }
    },
    setPhoneCallFieldIssues(state, action: PayloadAction<FieldIssue[]>) {
      state.phoneCallFieldIssues = action.payload;
    },
    setPhoneCallNonFieldErrors(state, action: PayloadAction<string[]>) {
      state.phoneCallNonFieldErrors = action.payload;
    },
    reset: () => initState({})
  },
  selectors: {
    selectPhoneCallContext: (state) => state.phoneCallContext,
    selectPhoneCallFieldIssues: (state) => state.phoneCallFieldIssues,
    selectPhoneCallNonFieldErrors: (state) => state.phoneCallNonFieldErrors
  }
});

export const {
  setPartialPhoneCallContext,
  setPhoneCallFieldIssues,
  setPhoneCallNonFieldErrors,
  normalizeTradeFromCompany,
  initialize,
  switchDifferentProviderCompany,
  reset
} = pointmanPhoneCallSlice.actions;

export const { selectPhoneCallContext, selectPhoneCallFieldIssues, selectPhoneCallNonFieldErrors } =
  pointmanPhoneCallSlice.getSelectors((rootState: RootState) => rootState.pointmanPhoneCallSlice);
